import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Button,
  Tooltip,
  Container,
  Typography,
  IconButton,
} from '@material-ui/core';

import i18n from 'i18n-js';
import { useQuery } from 'hooks';
import Can from 'components/Can';
import { useConfirm } from 'hooks';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import { ability } from 'config/ability';
import useStyles from './UserGroupsList.styled';
import { buildUrl, toQueryString } from 'utils';
import SearchIcon from '@material-ui/icons/Search';
import DeleteIcon from '@material-ui/icons/Delete';
import { SearchField } from 'components/FormFields';
import { userGroupsActions } from 'state/modules/userGroups';
import InputAdornment from '@material-ui/core/InputAdornment';
import { debounce } from 'lodash';

const UserGroupsList = () => {
  const classes = useStyles();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const history = useHistory();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const [groupId, setGroupId] = useState(null);
  const { userGroups, userGroupsLoading, pagination } = useSelector((state) => state.userGroups);
  const { fetchedDeleteUserGroupSuccess } = useSelector((state) => state.userGroups);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  const [toggleDeleteUserGroup, DeleteUserGroupConfirm] = useConfirm({
    title: i18n.t('user_groups.delete_user_group'),
    message: i18n.t('user_groups.delete_user_group_confirm'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => {
      dispatch(userGroupsActions.deleteUserGroup(groupId));
    },
  });

  const handleDeleteGroupModal = (id) => {
    setGroupId(id);
    toggleDeleteUserGroup();
  };

  const ActionsMenu = ({ row }) => {
    const handleClick = () => handleDeleteGroupModal(row?.id);
    return (
      <Tooltip title={i18n.t('user_groups.delete_user_group')}>
        <IconButton
          variant="contained"
          aria-haspopup="true"
          onClick={handleClick}
          aria-controls="card-menu"
          className={classes.cardHeaderActions}
          data-cy={`user-groups-list-delete-${row?.id}`}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const columns = [
    {
      name: 'ID',
      selector: 'id',
      sortable: true,
      grow: 0,
      minWidth: '4rem',
    },
    {
      name: 'Name',
      cell: (row) => row?.name || '--',
      selector: 'name',
      sortable: true,
    },
    {
      name: 'Type',
      cell: (row) => row?.user_group_type?.toLowerCase() || '--',
      selector: 'user_group_type',
      style: {
        textTransform: 'capitalize',
      },
      sortable: true,
    },
    {
      name: 'Status',
      selector: 'status',
      cell: (row) =>
        row?.status ? (
          <div className={`${classes.status} ${classes[row?.status.toLowerCase()]}`}>
            <span className="status">{row.status.toLowerCase()}</span>
          </div>
        ) : (
          '--'
        ),
      sortable: true,
    },
    {
      name: i18n.t('common.actions'),
      cell: (row) => <ActionsMenu row={row} />,
      button: true,
      allowOverflow: true,
      sortable: false,
      grow: 0,
      center: true,
    },
  ];

  const fetchUserGroups = useCallback(
    (params = {}) => {
      const _params = {
        ...params,
        is_customer: false,
        status_filter: 'exclude',
        user_group_type: ['BUSINESS'],
      };

      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        userGroupsActions.fetchUserGroups(_params, undefined, {
          signal: abortController?.current?.signal,
        })
      );
    },
    [dispatch]
  );

  const fetchUserGroupsDebounced = useCallback(
    debounce((params) => fetchUserGroups(params), 500),
    [fetchUserGroups]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchUserGroupsDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchUserGroupsDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchUserGroups(params);
  }, []);

  // On refetch delete/update success no debounce
  useEffect(() => {
    if (fetchedDeleteUserGroupSuccess) {
      fetchUserGroups(params);
      dispatch(userGroupsActions.resetDeleteUserGroup());
    }
  }, [fetchUserGroups, fetchedDeleteUserGroupSuccess]);

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/users-groups${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    if (ability.can('read', 'user_group')) {
      dispatch(userGroupsActions.resetOnboarding());
      history.push(buildUrl(`/users-groups/${row.id}`));
    }
  };

  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);
    handleParams({ ...filters });
  };

  return (
    <Layout title="User Groups">
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6" gutterBottom>
                  <Box fontWeight="fontWeightBold" data-cy="title" fontSize={24} mb={1}>
                    User Groups
                  </Box>
                </Typography>
              </Grid>
              <Can I="create" a="user_group">
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    data-cy="create-user-button"
                    onClick={() => {
                      dispatch(userGroupsActions.resetOnboarding());
                      history.push(buildUrl('/users-groups/create'));
                    }}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {i18n.t('user_groups.create')}
                  </Button>
                </Grid>
              </Can>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ paddingBottom: '25px' }}>
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={2}>
              <Grid item xs={12} sm={4} md={3}>
                <SearchField
                  label={'Search user group'}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  field={{
                    name: 'keyword',
                    onChange: search,
                    value: params.keyword,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={userGroups}
                columns={columns}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                loading={userGroupsLoading}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
              />
            </Paper>
          </Grid>
          <DeleteUserGroupConfirm />
        </Grid>
      </Container>
    </Layout>
  );
};

export default UserGroupsList;
